/**
 * Talks to the robot's Shelly switch directly over Wi-Fi (no internet): its RPC interface and the
 * Evesix script's endpoints (arm, stop, log). With a password set on the switch, requests carry
 * HTTP digest authentication (SHA-256, user "admin"), as Shelly Gen2 devices require.
 */

export interface HttpRequest {
  readonly url: string;
  readonly headers: Readonly<Record<string, string>>;
  readonly timeoutMs: number;
}

export interface HttpResponse {
  readonly status: number;
  /** Header names in lower case. */
  readonly headers: Readonly<Record<string, string>>;
  readonly body: string;
}

/** A GET over the local network. In the app: Capacitor's native HTTP (plain http to the LAN). */
export type HttpTransport = (request: HttpRequest) => Promise<HttpResponse>;

export class SwitchError extends Error {
  override readonly name = 'SwitchError';

  constructor(
    /** unreachable = no answer; auth = wrong password; device = the switch refused the command. */
    readonly kind: 'unreachable' | 'auth' | 'device',
    message: string,
  ) {
    super(message);
  }
}

/** Query values the switch script accepts: letters, digits, `,` `-` `_` `.`; never encoded. */
export type QueryValue = string | number | boolean;

function query(params: Readonly<Record<string, QueryValue>>): string {
  const parts = Object.entries(params).map(([key, value]) => {
    const text = String(value);
    if (!/^[A-Za-z0-9,._-]*$/.test(text)) throw new Error(`Unsafe value for ${key}: ${text}`);
    return `${key}=${text}`;
  });
  return parts.length ? `?${parts.join('&')}` : '';
}

const encoder = new TextEncoder();

async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', encoder.encode(text));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export interface DigestChallenge {
  readonly realm: string;
  readonly nonce: string;
}

/** Reads `WWW-Authenticate: Digest qop="auth", realm="…", nonce="…", algorithm=SHA-256`. */
export function parseDigestChallenge(header: string | undefined): DigestChallenge | null {
  if (!header || !/^Digest\s/i.test(header)) return null;
  const fields: Record<string, string> = {};
  for (const match of header.matchAll(/(\w+)=(?:"([^"]*)"|([^,\s]+))/g)) {
    fields[match[1]!.toLowerCase()] = match[2] ?? match[3] ?? '';
  }
  return fields.realm && fields.nonce ? { realm: fields.realm, nonce: fields.nonce } : null;
}

/** RFC 7616 digest response with SHA-256 and qop=auth, as Shelly Gen2 checks it. */
export async function digestAuthorization(options: {
  readonly challenge: DigestChallenge;
  readonly password: string;
  readonly uri: string;
  readonly nc: number;
  readonly cnonce: string;
}): Promise<string> {
  const { challenge, password, uri, cnonce } = options;
  const nc = options.nc.toString(16).padStart(8, '0');
  const ha1 = await sha256Hex(`admin:${challenge.realm}:${password}`);
  const ha2 = await sha256Hex(`GET:${uri}`);
  const response = await sha256Hex(`${ha1}:${challenge.nonce}:${nc}:${cnonce}:auth:${ha2}`);
  return (
    `Digest username="admin", realm="${challenge.realm}", nonce="${challenge.nonce}", ` +
    `uri="${uri}", algorithm=SHA-256, qop=auth, nc=${nc}, cnonce="${cnonce}", ` +
    `response="${response}"`
  );
}

export interface SwitchClientOptions {
  readonly timeoutMs?: number;
  /** Name the Evesix script was installed under on the switch. */
  readonly scriptName?: string;
  readonly cnonce?: () => string;
}

export class SwitchClient {
  readonly address: string;
  readonly #password: string | null;
  readonly #transport: HttpTransport;
  readonly #timeoutMs: number;
  readonly #scriptName: string;
  readonly #cnonce: () => string;
  #challenge: DigestChallenge | null = null;
  #nc = 0;
  /** The script's id, looked up once and shared by calls made while the lookup runs. */
  #scriptId: Promise<number> | null = null;

  constructor(
    address: string,
    password: string | null,
    transport: HttpTransport,
    {
      timeoutMs = 4000,
      scriptName = 'evesix',
      cnonce = () => crypto.randomUUID().replaceAll('-', ''),
    }: SwitchClientOptions = {},
  ) {
    this.address = address;
    this.#password = password;
    this.#transport = transport;
    this.#timeoutMs = timeoutMs;
    this.#scriptName = scriptName;
    this.#cnonce = cnonce;
  }

  /** A Shelly RPC method, e.g. `Switch.Set` with `{ id: 0, on: false }`. */
  async rpc<T>(method: string, params: Readonly<Record<string, QueryValue>> = {}): Promise<T> {
    const response = await this.#get(`/rpc/${method}${query(params)}`);
    if (response.status < 200 || response.status >= 300) {
      throw new SwitchError('device', `${method}: ${errorMessage(response)}`);
    }
    return parse<T>(response.body);
  }

  /** One of the Evesix script's endpoints. Its 4xx answers (busy, log full) are returned, not thrown. */
  async script<T>(
    endpoint: 'arm' | 'stop' | 'log',
    params: Readonly<Record<string, QueryValue>> = {},
  ): Promise<{ readonly status: number; readonly body: T }> {
    const lookup = (this.#scriptId ??= this.#findScript());
    let scriptId: number;
    try {
      scriptId = await lookup;
    } catch (error) {
      if (this.#scriptId === lookup) this.#scriptId = null; // look again next time
      throw error;
    }
    const response = await this.#get(`/script/${scriptId}/${endpoint}${query(params)}`);
    // Reinstalled under another id.
    if (response.status === 404 && this.#scriptId === lookup) this.#scriptId = null;
    if (response.status >= 500 || response.status === 404) {
      throw new SwitchError('device', `script ${endpoint}: ${errorMessage(response)}`);
    }
    return { status: response.status, body: parse<T>(response.body) };
  }

  async #findScript(): Promise<number> {
    const { scripts } = await this.rpc<{ scripts: { id: number; name: string }[] }>('Script.List');
    const script = scripts.find((s) => s.name === this.#scriptName);
    if (!script) {
      throw new SwitchError('device', `The "${this.#scriptName}" script is not installed`);
    }
    return script.id;
  }

  async #get(uri: string): Promise<HttpResponse> {
    let response = await this.#send(uri);
    if (response.status === 401 && this.#password !== null) {
      // First contact, or the nonce went stale: answer the challenge once.
      this.#challenge = parseDigestChallenge(response.headers['www-authenticate']);
      this.#nc = 0;
      if (this.#challenge) response = await this.#send(uri);
    }
    if (response.status === 401) throw new SwitchError('auth', 'The switch refused the password');
    return response;
  }

  async #send(uri: string): Promise<HttpResponse> {
    const headers: Record<string, string> = {};
    if (this.#challenge && this.#password !== null) {
      headers.authorization = await digestAuthorization({
        challenge: this.#challenge,
        password: this.#password,
        uri,
        nc: ++this.#nc,
        cnonce: this.#cnonce(),
      });
    }
    try {
      return await this.#transport({
        url: `http://${this.address}${uri}`,
        headers,
        timeoutMs: this.#timeoutMs,
      });
    } catch (error) {
      throw new SwitchError('unreachable', `No answer from ${this.address}: ${String(error)}`);
    }
  }
}

function parse<T>(body: string): T {
  return (body ? JSON.parse(body) : null) as T;
}

function errorMessage(response: HttpResponse): string {
  try {
    const body = JSON.parse(response.body) as { message?: string; error?: string };
    return body.message ?? body.error ?? `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}
